<template>
  <div class="card audit-trail">
    <PageHeader
        title="Audit Trail"
        subtitle="Who created and changed records"
        :showBtn="false"
    />
    <div class="audit-trail-body">
      <AuditTrailToolbar
          :filters="filters"
          :users="users"
          @apply="onApplyFilters"
          @clear="onClearFilters"
          @remove-filter="onRemoveFilters"
          @export="onClickExport"
      />
      <AuditTrailSummaryCards :summary="summary" :loading="loading" />
      <AuditTrailTable
          :rows="rows"
          :loading="loading"
          :sort="filters.sort"
          :dir="filters.dir"
          :printable-types="printableTypes"
          @sort="onApplyFilters"
          @view="onViewHistory"
          @print="onPrintVoucher"
      />
      <div class="audit-trail-pagination">
        <span class="audit-trail-count">{{ resultText }}</span>
        <Pagination :offset="offset" @onPageChange="onPageChange" />
      </div>
    </div>
    <AuditTrailDetailDrawer
        :record-id="viewId"
        :printable-types="printableTypes"
        @close="onCloseHistory"
        @print="onPrintVoucher"
    />
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/atom/PageHeader.vue'
import Pagination from '@/components/atom/Pagination'
import AuditTrailToolbar from '@/components/molecule/company/audit-trail/AuditTrailToolbar.vue'
import AuditTrailSummaryCards from '@/components/molecule/company/audit-trail/AuditTrailSummaryCards.vue'
import AuditTrailTable from '@/components/molecule/company/audit-trail/AuditTrailTable.vue'
import AuditTrailDetailDrawer from '@/components/molecule/company/audit-trail/AuditTrailDetailDrawer.vue'
import handleAuditTrail from '@/services/modules/auditTrail'
import handleDropdowns from '@/services/modules/dropdowns'
import { useVoucherPrint } from '@/services/utils/voucherPrint'

const $store = useStore()
const $route = useRoute()
const $router = useRouter()
const showError = inject('showError')
const { fetchAuditTrail, getAuditTrailExportUrl } = handleAuditTrail()
const { fetchDropdownList } = handleDropdowns()
const { printVoucher, vouchers } = useVoucherPrint()

const filterKeys = ['q', 'quick', 'created_by', 'updated_by', 'created_start', 'created_end', 'updated_start', 'updated_end', 'sort', 'dir']
const clearableKeys = ['q', 'quick', 'created_by', 'updated_by', 'created_start', 'created_end', 'updated_start', 'updated_end']

const offset = 20
const loading = ref(false)
const rows = ref([])
const summary = ref({})
const users = ref([])
const total = ref(0)

const companyId = computed(() => $route.params.companyId)
const viewId = computed(() => $route.query.view || '')
const printableTypes = Object.keys(vouchers)

const filters = computed(() => {
  return filterKeys.reduce((result, key) => {
    result[key] = $route.query[key] || ''
    return result
  }, {})
})

const resultText = computed(() => {
  if (! total.value) return ''
  return `${total.value.toLocaleString('en-IN')} records`
})

const getFilterQuery = () => {
  return filterKeys.reduce((query, key) => {
    if (! filters.value[key]) return query
    return query + `&${key}=${encodeURIComponent(filters.value[key])}`
  }, `?company_id=${companyId.value}`)
}

const cleanQuery = (query) => {
  return Object.keys(query).reduce((result, key) => {
    if (query[key] === '' || query[key] === null || query[key] === undefined) return result
    result[key] = query[key]
    return result
  }, {})
}

const pushQuery = async (query) => {
  await $router.push({ path: $route.path, query: cleanQuery(query) })
}

const setPagination = (data) => {
  $store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page
  })
}

const getAuditTrail = async () => {
  loading.value = true
  const page = $route.query.page || 1
  try {
    const res = await fetchAuditTrail(getFilterQuery() + `&offset=${offset}&page=${page}`)
    if (! res.status) {
      showError(res.message)
      return
    }
    rows.value = res.data.data
    total.value = res.data.total
    summary.value = res.summary
    setPagination(res.data)
  } catch (err) {
    showError('Something went wrong')
  } finally {
    loading.value = false
  }
}

const getUsers = async () => {
  try {
    const res = await fetchDropdownList(`?company_id=${companyId.value}&is_company_users=1`)
    if (! res.status || ! res.data) return
    users.value = res.data.company_user_list
  } catch (err) {
    showError('Something went wrong')
  }
}

const onApplyFilters = async (patch) => {
  await pushQuery(Object.assign({}, $route.query, patch, { page: 1, view: '' }))
  getAuditTrail()
}

const onRemoveFilters = async (keys) => {
  const patch = keys.reduce((result, key) => {
    result[key] = ''
    return result
  }, {})
  await onApplyFilters(patch)
}

const onClearFilters = async () => {
  await onRemoveFilters(clearableKeys)
}

const onPageChange = async (page) => {
  await pushQuery(Object.assign({}, $route.query, { page }))
  getAuditTrail()
}

const onViewHistory = async (row) => {
  await pushQuery(Object.assign({}, $route.query, { view: row.id }))
}

const onCloseHistory = async () => {
  await pushQuery(Object.assign({}, $route.query, { view: '' }))
}

const onPrintVoucher = (record) => {
  if (! printableTypes.includes(record.voucher_type)) return
  printVoucher(record.id, record.voucher_type)
}

const onClickExport = () => {
  const anchor = document.createElement('a')
  anchor.setAttribute('href', getAuditTrailExportUrl(getFilterQuery()))
  anchor.click()
}

onMounted(() => {
  getAuditTrail()
  getUsers()
})
</script>

<style scoped>
.audit-trail {
  min-height: calc(100vh - 185px);
}
.audit-trail-body {
  padding: 16px 20px 20px;
}
.audit-trail-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 12px;
}
.audit-trail-count {
  font-size: 13px;
  color: #667085;
}
</style>
